// ADR-119: Organizational Structure Types — admin-managed classification of
// Clusters. Structural clone of OrgUnitTypes (which classifies business
// units / positions); the two taxonomies are intentionally independent.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEye, FaEyeSlash, FaPlus, FaSearch } from 'react-icons/fa';
import { 
  TextField, 
  Button, 
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  InputAdornment
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, BackButton, HeaderButton, ControlsContainer, MainContainer } from '../../common/styles';
import ConfirmationModal from '../../common/ConfirmationModal';
import Modal from '../../common/Modal';
import LoadingSpinner from '../../common/LoadingSpinner';
import { useAlert } from '../../../contexts/AlertContext';
import { organizationApi } from '../../../services/api';
import { useAbility } from '../../../contexts/AbilityContext';
import { ACTIONS, SUBJECTS } from '../../../constants/permissions';

const OrgStructureTypesConfiguration = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const ability = useAbility();
  const [showControls, setShowControls] = useState(true);
  const [orgStructureTypes, setOrgStructureTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  // Unified modal state
  const [orgStructureTypeModal, setOrgStructureTypeModal] = useState({ 
    open: false, 
    isEdit: false, 
    formData: { name: '', code: '', description: '' },
    originalId: null
  });
  
  const [deleteModal, setDeleteModal] = useState({ open: false, orgStructureType: null });

  const fetchOrgStructureTypes = async () => {
    try {
      setIsLoading(true);
      const data = await organizationApi.getOrgStructureTypes();
      setOrgStructureTypes(data);
    } catch (err) {
      showAlert('Failed to load organizational structure types.', 'error', err);
      console.error('Error fetching organizational structure types:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrgStructureTypes();
  }, []);

  // Unified handler for both add and edit
  const handleSaveOrgStructureType = async (e) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);
      
      if (orgStructureTypeModal.isEdit) {
        // Edit existing organizational structure type
        if (!orgStructureTypeModal.originalId) {
          showAlert('Invalid organizational structure type data for update', 'error');
          return;
        }
        
        await organizationApi.updateOrgStructureType(orgStructureTypeModal.originalId, {
          name: orgStructureTypeModal.formData.name,
          code: orgStructureTypeModal.formData.code,
          description: orgStructureTypeModal.formData.description || null
        });
        showAlert('Organizational structure type updated successfully', 'success');
      } else {
        // Add new organizational structure type
        await organizationApi.createOrgStructureType({
          name: orgStructureTypeModal.formData.name,
          code: orgStructureTypeModal.formData.code,
          description: orgStructureTypeModal.formData.description || null
        });
        showAlert('Organizational structure type added successfully', 'success');
      }
      
      await fetchOrgStructureTypes();
      // Reset modal state
      setOrgStructureTypeModal({
        open: false,
        isEdit: false,
        formData: { name: '', code: '', description: '' },
        originalId: null
      });
    } catch (err) {
      const action = orgStructureTypeModal.isEdit ? 'update' : 'add';
      showAlert(err.message || `Failed to ${action} organizational structure type.`, 'error', err);
      console.error(`Error ${action}ing organizational structure type:`, err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteOrgStructureType = (orgStructureType) => {
    setDeleteModal({ open: true, orgStructureType });
  };

  const confirmDelete = async () => {
    const typeToDelete = deleteModal.orgStructureType;
    setDeleteModal({ open: false, orgStructureType: null });
    
    if (!typeToDelete?.id) {
      showAlert('Invalid organizational structure type data', 'error');
      return;
    }

    try {
      setIsLoading(true);
      await organizationApi.deleteOrgStructureType(typeToDelete.id);
      await fetchOrgStructureTypes();
      showAlert('Organizational structure type deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting organizational structure type:', err);
      showAlert(err.message || 'Failed to delete organizational structure type.', 'error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredOrgStructureTypes = orgStructureTypes.filter(type => {
    if (!searchTerm) return true;
    const lowerSearch = searchTerm.toLowerCase();
    return type.name.toLowerCase().includes(lowerSearch) || 
           (type.code && type.code.toLowerCase().includes(lowerSearch)) ||
           (type.description && type.description.toLowerCase().includes(lowerSearch));
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/organization')}>
            <FaArrowLeft /> Back to Organization
          </BackButton>
          <Title>Organizational Structure Types Configuration</Title>
        </HeaderLeft>
        <HeaderButton onClick={() => setShowControls(!showControls)}>
          {showControls ? <FaEyeSlash /> : <FaEye />}
          {showControls ? 'Hide Panels' : 'Show Panels'}
        </HeaderButton>
      </Header>

      <Content>
        <div style={{ flex: 1 }}>
          <ControlsContainer $show={showControls} style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between' }}>
            <TextField
              placeholder="Search Organizational Structure Type"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }
              }}
              sx={{
                minWidth: '300px',
              }}
            />
            {ability.can(ACTIONS.CREATE, SUBJECTS.ORG_STRUCTURE_TYPE) && (
              <HeaderButton onClick={() => setOrgStructureTypeModal({
                open: true,
                isEdit: false,
                formData: { name: '', code: '', description: '' },
                originalId: null
              })}>
                <FaPlus /> Add Organizational Structure Type
              </HeaderButton>
            )}
          </ControlsContainer>

          <MainContainer $show={showControls}>
            <div style={{ 
              width: '100%',
              height: '100%',
              overflowY: 'auto',
              padding: '24px'
            }}>
              <div style={{ 
                color: '#64748B', 
                fontSize: '14px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <span style={{ fontWeight: 600 }}>
                  {filteredOrgStructureTypes.length}
                </span>
                {filteredOrgStructureTypes.length === 1 ? 'organizational structure type' : 'organizational structure types'} found
              </div>
              <Grid 
                container 
                spacing={3}
                sx={{
                  width: '100%',
                  margin: '0 auto',
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(4, 1fr)',
                    xl: 'repeat(4, 1fr)'
                  },
                  gap: '24px',
                  '& .MuiGrid-item': {
                    padding: '0',
                    width: '100%'
                  }
                }}
              >
                {filteredOrgStructureTypes.map((orgStructureType) => (
                  <Grid item key={orgStructureType.id}>
                    <Card 
                      sx={{ 
                        width: '100%',
                        minHeight: '150px',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.2s',
                        border: '1px solid #e0e0e0',
                        boxShadow: 'none',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        backgroundColor: '#f8f9fa',
                        '&:hover': {
                          borderColor: '#bdbdbd',
                          backgroundColor: '#f1f3f5',
                        }
                      }}
                    >
                      <CardContent sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        padding: '16px !important',
                        flexGrow: 1,
                      }}>
                        <Box>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 500,
                              color: '#212529',
                              marginBottom: '4px'
                            }}
                          >
                            {orgStructureType.name}
                          </Typography>
                          {orgStructureType.code && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#868e96',
                                fontFamily: 'monospace',
                                fontSize: '12px',
                                display: 'block',
                                marginBottom: '8px'
                              }}
                            >
                              Code: {orgStructureType.code}
                            </Typography>
                          )}
                          {orgStructureType.description && (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#6c757d',
                                marginBottom: '8px'
                              }}
                            >
                              {orgStructureType.description}
                            </Typography>
                          )}
                          {orgStructureType._count?.clusters > 0 && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#868e96',
                                fontStyle: 'italic'
                              }}
                            >
                              Used by {orgStructureType._count.clusters} cluster{orgStructureType._count.clusters !== 1 ? 's' : ''}
                            </Typography>
                          )}
                        </Box>

                        <Box sx={{ 
                          display: 'flex',
                          justifyContent: 'flex-end',
                          gap: '8px',
                          marginTop: '16px'
                        }}>
                          {ability.can(ACTIONS.UPDATE, SUBJECTS.ORG_STRUCTURE_TYPE) && (
                            <Button
                              onClick={() => setOrgStructureTypeModal({
                                open: true,
                                isEdit: true,
                                formData: { 
                                  name: orgStructureType.name,
                                  code: orgStructureType.code || '',
                                  description: orgStructureType.description || ''
                                },
                                originalId: orgStructureType.id
                              })}
                              sx={{
                                minWidth: 'unset',
                                padding: '6px 12px',
                                fontSize: '14px',
                                color: '#0d6efd',
                                transition: 'all 0.2s ease',
                                backgroundColor: 'rgba(13, 110, 253, 0.1)',
                                textTransform: 'none',
                                '&:hover': {
                                  backgroundColor: 'rgba(13, 110, 253, 0.15)',
                                  color: '#0d6efd'
                                }
                              }}
                            >
                              Edit
                            </Button>
                          )}
                          {ability.can(ACTIONS.DELETE, SUBJECTS.ORG_STRUCTURE_TYPE) && (
                            <Button
                              onClick={() => handleDeleteOrgStructureType(orgStructureType)}
                              sx={{
                                minWidth: 'unset',
                                padding: '6px 12px',
                                fontSize: '14px',
                                color: '#dc3545',
                                transition: 'all 0.2s ease',
                                backgroundColor: 'rgba(220, 53, 69, 0.1)',
                                textTransform: 'none',
                                '&:hover': {
                                  backgroundColor: 'rgba(220, 53, 69, 0.15)',
                                  color: '#dc3545'
                                }
                              }}
                            >
                              Delete
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </div>
          </MainContainer>
        </div>
      </Content>

      {/* Unified modal for both adding and editing organizational structure types */}
      <Modal
        isOpen={orgStructureTypeModal.open}
        onClose={() => setOrgStructureTypeModal({
          open: false,
          isEdit: false,
          formData: { name: '', code: '', description: '' },
          originalId: null
        })}
        onSubmit={handleSaveOrgStructureType}
        title={orgStructureTypeModal.isEdit ? "Edit Organizational Structure Type" : "Add Organizational Structure Type"}
        submitText={orgStructureTypeModal.isEdit ? "Update" : "Add"}
      >
        <Box mb={2}>
          <TextField
            id="org-structure-type-name"
            name="name"
            label="Organizational Structure Type Name"
            required
            value={orgStructureTypeModal.formData.name}
            onChange={(e) => setOrgStructureTypeModal(prev => ({
              ...prev,
              formData: { ...prev.formData, name: e.target.value }
            }))}
            placeholder="Enter organizational structure type name"
            autoComplete="off"
            fullWidth
            variant="outlined"
          />
        </Box>
        <Box mb={2}>
          <TextField
            id="org-structure-type-code"
            name="code"
            label="Code"
            required
            value={orgStructureTypeModal.formData.code}
            onChange={(e) => setOrgStructureTypeModal(prev => ({
              ...prev,
              formData: { ...prev.formData, code: e.target.value }
            }))}
            placeholder="Enter organizational structure type code"
            autoComplete="off"
            fullWidth
            variant="outlined"
          />
        </Box>
        <Box mb={2}>
          <TextField
            id="org-structure-type-description"
            name="description"
            label="Description"
            value={orgStructureTypeModal.formData.description}
            onChange={(e) => setOrgStructureTypeModal(prev => ({
              ...prev,
              formData: { ...prev.formData, description: e.target.value }
            }))}
            placeholder="Enter description (optional)"
            autoComplete="off"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
          />
        </Box>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, orgStructureType: null })}
        onConfirm={confirmDelete}
        title="Delete Organizational Structure Type"
        message={`Are you sure you want to delete the organizational structure type "${deleteModal.orgStructureType?.name}"? This action cannot be undone.`}
      />
    </Container>
  );
};

export default OrgStructureTypesConfiguration;